import { StatusEnum } from '@/types';
import { ATTACHMENTS_TOOLBAR_ACTION } from './AttachmentsToolbar.keys';

import type { AttachmentUploadContext } from './AttachmentsToolbar.types';

export const attachmentsToolbarReducerInitialState = {
  attachments: [],
  count: {
    total: 0,
    initializing: 0,
    uploading: 0,
    uploaded: 0,
    failed: 0,
    waiting: 0,
  },
} as Omit<AttachmentUploadContext, 'dispatch'>;

export const getAttachmentsCountByStatus = (
  attachments: AttachmentUploadContext['attachments'] = [],
) =>
  attachments.reduce(
    (acc, item) => {
      switch (item?.status) {
        case StatusEnum.PENDING:
          if (item?.pre_signed_url) {
            acc.uploading++;
          } else {
            acc.initializing++;
          }
          break;
        case StatusEnum.DONE:
          acc.uploaded++;
          break;
        case StatusEnum.FAILED:
          acc.failed++;
          break;
        default:
          acc.waiting++;
          break;
      }

      return acc;
    },
    {
      total: attachments.length,
      initializing: 0,
      uploading: 0,
      uploaded: 0,
      failed: 0,
      waiting: 0,
    },
  );

const updateWithCount = (attachments) => ({
  attachments,
  count: getAttachmentsCountByStatus(attachments),
});

/**
 * Form submit re-marks attachments PENDING so S3 upload can start. After a
 * Chart of Accounts file is uploaded for replace-validation it is already
 * DONE with a pre-signed URL; kicking PENDING again leaves the toast at 100%
 * because useUploadPendingAttachment will not re-send a file that has a URL.
 */
export const shouldPreserveCompletedUpload = (
  currentStatus: unknown,
  nextStatus: unknown,
): boolean =>
  currentStatus === StatusEnum.DONE && nextStatus === StatusEnum.PENDING;

export const attachmentsToolbarReducer =
  (uploadControllers) =>
  (state: Omit<AttachmentUploadContext, 'dispatch'>, action) => {
    switch (action.type) {
      case ATTACHMENTS_TOOLBAR_ACTION.ADD: {
        return updateWithCount([action.payload, ...state.attachments]);
      }
      case ATTACHMENTS_TOOLBAR_ACTION.ADD_MANY: {
        return updateWithCount([...action.payload, ...state.attachments]);
      }
      case ATTACHMENTS_TOOLBAR_ACTION.UPDATE: {
        const { attachment_id, ...update } = action.payload;

        const attachments = state.attachments.map((item) => {
          if (item?.attachment_id === attachment_id) {
            return {
              ...item,
              ...update,
            };
          }

          return item;
        });

        return updateWithCount(attachments);
      }
      case ATTACHMENTS_TOOLBAR_ACTION.UPDATE_MANY: {
        const { attachment_ids, ...update } = action.payload;

        const attachments = state.attachments.map((item) => {
          if (attachment_ids.includes(item?.attachment_id)) {
            return {
              ...item,
              ...update,
            };
          }

          return item;
        });

        return updateWithCount(attachments);
      }
      case ATTACHMENTS_TOOLBAR_ACTION.UPDATE_STATUS_MANY: {
        const { attachment_ids, ...update } = action.payload;

        const attachments = state.attachments.map((item) => {
          if (!attachment_ids.includes(item?.attachment_id)) {
            return item;
          }

          if (shouldPreserveCompletedUpload(item?.status, update.status)) {
            return item;
          }

          return {
            ...item,
            ...update,
          };
        });

        return updateWithCount(attachments);
      }
      case ATTACHMENTS_TOOLBAR_ACTION.UPDATE_STATUS: {
        const { attachment_id, status } = action.payload;

        const attachments = state.attachments.map((item) => {
          if (item?.attachment_id !== attachment_id) {
            return item;
          }

          if (shouldPreserveCompletedUpload(item?.status, status)) {
            return item;
          }

          return {
            ...item,
            status,
          };
        });

        return updateWithCount(attachments);
      }
      case ATTACHMENTS_TOOLBAR_ACTION.UPDATE_PROGRESS: {
        const { attachment_id, progress } = action.payload;

        const attachments = state.attachments.map((item) => {
          if (item?.attachment_id === attachment_id) {
            return {
              ...item,
              progress,
            };
          }

          return item;
        });

        return updateWithCount(attachments);
      }
      case ATTACHMENTS_TOOLBAR_ACTION.REMOVE: {
        uploadControllers?.[action.payload.attachment_id]?.();
        const attachments = state.attachments.filter(
          (item) => item?.attachment_id !== action.payload.attachment_id,
        );

        return updateWithCount(attachments);
      }
      case ATTACHMENTS_TOOLBAR_ACTION.REMOVE_ALL: {
        return updateWithCount([]);
      }
      default: {
        throw Error(`Unknown action: ${action.type}`);
      }
    }
  };
